import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Anchor, Bed, MapPin, Calendar, Ship, Check, X, Sparkles, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

function fmt(n) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

export default function OfferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    api.get(`/offers/${id}`).then((r) => setOffer(r.data)).catch(() => {});
  }, [id]);

  if (!offer) return <div className="min-h-screen pt-32 text-center text-ink">Loading…</div>;

  const reasons = [
    lang === "it" ? "Rispetta il tuo budget complessivo" : "Within your total budget",
    lang === "it" ? `Disponibile nella destinazione ${offer.destination}` : `Available in ${offer.destination}`,
    lang === "it" ? `Spazio sufficiente per il tuo gruppo (${offer.beds} posti)` : `Enough space for your group (${offer.beds} beds)`,
  ];

  return (
    <div data-testid="offer-detail" className="bg-white pt-20 pb-20">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px]">
        <img src={offer.image_url} alt={offer.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10 h-full flex flex-col justify-end pb-10">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="self-start inline-flex items-center gap-2 text-white/90 hover:text-white text-sm"
          >
            <ArrowLeft className="h-4 w-4" /> {t("detail.back")}
          </button>
          <p className="mt-6 label-eyebrow text-gold">{offer.boat_type} · {offer.comfort_level}</p>
          <h1 className="mt-3 font-serif text-4xl lg:text-6xl text-white tracking-tight max-w-3xl">{offer.name}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-white/85 text-sm">
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {offer.destination} · {offer.area}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {offer.start_date} → {offer.end_date}</span>
            <span className="inline-flex items-center gap-1.5"><Bed className="h-4 w-4" /> {offer.beds} {t("common.beds")}</span>
            <span className="inline-flex items-center gap-1.5"><Anchor className="h-4 w-4" /> {offer.skipper_included ? t("common.withSkipper") : t("common.noSkipper")}</span>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-10 mt-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-12">
          <section>
            <p className="text-base text-ink leading-relaxed">{offer.description}</p>
          </section>

          {/* Why we recommend */}
          <section data-testid="why-section" className="rounded-2xl bg-sand p-7 border border-[#E5E1D2]">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-gold" />
              <p className="label-eyebrow">{t("detail.why")}</p>
            </div>
            <ul className="mt-5 space-y-3">
              {reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-navy text-white"><Check className="h-3 w-3" /></span>
                  <span className="text-navy">{r}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Itinerary */}
          {offer.itinerary?.length > 0 && (
            <section>
              <h2 className="font-serif text-2xl text-navy">{t("detail.itinerary")}</h2>
              <ol className="mt-5 grid sm:grid-cols-2 gap-3">
                {offer.itinerary.map((stop, i) => (
                  <li key={i} className="flex items-center gap-3 p-3 rounded-lg border border-[#E2E8F0]">
                    <span className="font-serif text-2xl text-gold/80 w-8">{i + 1}</span>
                    <span className="text-sm text-navy">{stop}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* Included / Excluded */}
          <section className="grid sm:grid-cols-2 gap-6">
            <div>
              <h3 className="font-serif text-xl text-navy">{t("detail.included")}</h3>
              <ul className="mt-3 space-y-2">
                {(offer.included || []).map((v) => (
                  <li key={v} className="flex items-center gap-2 text-sm text-navy">
                    <Check className="h-4 w-4 text-turquoise" /> {v}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-serif text-xl text-navy">{t("detail.excluded")}</h3>
              <ul className="mt-3 space-y-2">
                {(offer.excluded || []).map((v) => (
                  <li key={v} className="flex items-center gap-2 text-sm text-ink">
                    <X className="h-4 w-4 text-ink/60" /> {v}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-5">
          <div className="sticky top-24 rounded-2xl border border-[#E2E8F0] p-6 bg-white">
            <p className="text-xs uppercase tracking-wider text-ink">{t("common.total")}</p>
            <p className="font-serif text-5xl text-navy">{fmt(offer.total_price)}</p>
            <p className="text-sm text-ink">{fmt(offer.price_per_person)} {t("common.perPerson")} · {offer.duration_days} {t("common.nights")}</p>

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-sand p-3">
                <p className="text-[11px] uppercase tracking-wider text-ink">Cabine</p>
                <p className="font-medium text-navy">{offer.cabins}</p>
              </div>
              <div className="rounded-lg bg-sand p-3">
                <p className="text-[11px] uppercase tracking-wider text-ink">Bagni</p>
                <p className="font-medium text-navy">{offer.bathrooms}</p>
              </div>
              <div className="rounded-lg bg-sand p-3">
                <p className="text-[11px] uppercase tracking-wider text-ink">{lang === "it" ? "Charter" : "Charter"}</p>
                <p className="font-medium text-navy truncate">{offer.charter_company}</p>
              </div>
              <div className="rounded-lg bg-sand p-3">
                <p className="text-[11px] uppercase tracking-wider text-ink">{lang === "it" ? "Comfort" : "Comfort"}</p>
                <p className="font-medium text-navy capitalize">{offer.comfort_level}</p>
              </div>
            </div>

            <Button
              data-testid="cta-request"
              onClick={() => toast.success(lang === "it" ? "Richiesta inviata! Ti contattiamo entro 24h." : "Request sent! We'll be in touch within 24h.")}
              className="mt-6 w-full bg-navy text-white hover:bg-[#06101f] h-12 rounded-md"
            >
              {t("detail.cta")}
            </Button>
            <Button
              data-testid="cta-advisor"
              variant="outline"
              onClick={() => toast.info(lang === "it" ? "Un consulente ti chiamerà presto." : "An advisor will call you soon.")}
              className="mt-3 w-full border-navy/30 text-navy hover:bg-sand h-12 rounded-md"
            >
              <Phone className="h-4 w-4 mr-2" /> {t("detail.cta2")}
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}
